import React, { useCallback, useEffect, useState } from "react";
import apiService from "../../services/apiService";
import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  message,
  Spin,
  Breadcrumb,
  Divider,
  Tag,
  Alert,
  Space,
} from "antd";
import {
  SafetyCertificateOutlined,
  LockOutlined,
  UnlockOutlined,
  CopyOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useHeader } from "../../components/context/HeaderContext";

function Profile({ title }) {
  const { setHeaderTitle } = useHeader();

  const [form] = Form.useForm();
  const [profileData, setProfileData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    errors: [],
    loading: false,
  });
  const [buttonLoading, setButtonLoading] = useState(false);

  // ── MFA state ──────────────────────────────────────────────────────────────
  const [mfaEnabled, setMfaEnabled]             = useState(false);
  const [mfaLoading, setMfaLoading]             = useState(false);
  const [mfaQrCode, setMfaQrCode]               = useState(null);   // base64 QR image
  const [mfaSecret, setMfaSecret]               = useState(null);   // manual entry fallback
  const [mfaStep, setMfaStep]                   = useState('idle'); // 'idle' | 'scan' | 'confirm' | 'backup_codes'
  const [setupOtp, setSetupOtp]                 = useState('');
  const [setupOtpError, setSetupOtpError]       = useState('');
  const [displayedBackupCodes, setDisplayedBackupCodes] = useState([]);

  // Disable modal (UI trigger commented out)
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [disableOtp, setDisableOtp]             = useState('');
  const [disableOtpError, setDisableOtpError]   = useState('');
  const [disableLoading, setDisableLoading]     = useState(false);

  // ── Fetch profile (existing business logic) ────────────────────────────────
  const fetchRecords = useCallback(async () => {
    try {
      setProfileData((prev) => ({ ...prev, loading: true }));
      const response = await apiService.get("auth/profile");
      if (response.status === 200) {
        form.setFieldsValue({
          first_name: response.data.first_name,
          last_name:  response.data.last_name,
          email:      response.data.email,
          phone:      response.data.phone,
        });
        setProfileData((prevData) => ({ ...prevData, loading: false, ...response.data }));
      }
    } catch (error) {
      setProfileData((prevData) => ({ ...prevData, loading: false, errors: [] }));
      message.error(error.response?.statusText || "Failed to load profile");
    }
  }, [form]);

  // ── Fetch MFA status ───────────────────────────────────────────────────────
  const fetchMfaStatus = useCallback(async () => {
    try {
      const response = await apiService.get("auth/mfa/status");
      if (response.status === 200) {
        setMfaEnabled(Boolean(response.data.mfa_enabled));
      }
    } catch (error) {
      // Non-critical background fetch
    }
  }, []);

  useEffect(() => {
    fetchRecords();
    fetchMfaStatus();
  }, [fetchRecords, fetchMfaStatus]);

  const handleInput = (e) => {
    e.persist();
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
  };

  // ── UpdateProfile (existing business logic) ────────────────────────────────
  const UpdateProfile = async (e) => {
    e.preventDefault();
    const data = {
      first_name: profileData.first_name,
      last_name:  profileData.last_name,
      email:      profileData.email,
      phone:      profileData.phone,
    };
    setProfileData((prev) => ({ ...prev, loading: true }));
    setButtonLoading(true);
    try {
      const response = await apiService.post("auth/update-profile", data);
      if (response.status === 200) {
        const { id, first_name, last_name, email, phone } = data;
        localStorage.setItem("user", JSON.stringify({ id, first_name, last_name, email, phone }));
        setHeaderTitle(`${first_name} ${last_name}`);
        message.success(response.data.message);
        setProfileData({ ...profileData, loading: false, errors: [] });
        setButtonLoading(false);
      }
    } catch (error) {
      setProfileData((prev) => ({ ...prev, loading: false }));
      setButtonLoading(false);
      if (error.response) {
        if (error.response.status === 422) {
          setProfileData({ ...profileData, errors: error.response.data.errors });
        } else if (error.response.status === 500) {
          setProfileData({ ...profileData, errors: [] });
          message.error(error.response.data.message);
        } else {
          message.error("Something went wrong. Please try again later.");
        }
      } else {
        message.error("Some Problem Occured! Please try again later.");
      }
    }
  };

  // ── MFA: Start setup (fetch QR) ────────────────────────────────────
  const handleMfaSetup = async () => {
    setMfaLoading(true);
    try {
      const response = await apiService.get("auth/mfa/setup");
      if (response.status === 200) {
        setMfaQrCode(response.data.qr_code);
        setMfaSecret(response.data.secret);
        setMfaStep('scan');
      }
    } catch (error) {
      message.error("Failed to initiate MFA setup. Please try again.");
    } finally {
      setMfaLoading(false);
    }
  };

  // ── MFA: Verify OTP and show initial backup recovery codes ─────────────────
  const handleVerifySetup = async (e) => {
    e.preventDefault();
    if (!setupOtp || setupOtp.length !== 6) {
      setSetupOtpError("Please enter the 6-digit code from your authenticator app.");
      return;
    }
    setMfaLoading(true);
    setSetupOtpError('');
    try {
      const response = await apiService.post("auth/mfa/verify-setup", { otp: setupOtp });
      if (response.status === 200) {
        message.success("Two-Factor Authentication enabled successfully!");
        setMfaEnabled(true);
        setDisplayedBackupCodes(response.data.backup_codes || []);
        setMfaStep('backup_codes');
        setMfaQrCode(null);
        setMfaSecret(null);
        setSetupOtp('');
      }
    } catch (error) {
      if (error.response?.status === 422) {
        setSetupOtpError(error.response.data.errors?.otp?.message || "Invalid OTP code.");
      } else {
        message.error(error.response?.data?.message || "Failed to verify OTP. Please try again.");
      }
    } finally {
      setMfaLoading(false);
    }
  };

  // ── MFA: Disable ──────────────────────────────────────────────────────────
  const handleDisable = async () => {
    if (!disableOtp || disableOtp.length !== 6) {
      setDisableOtpError("Please enter the 6-digit code from your authenticator app.");
      return;
    }
    setDisableLoading(true);
    setDisableOtpError('');
    try {
      const response = await apiService.post("auth/mfa/disable", { otp: disableOtp });
      if (response.status === 200) {
        message.success("Two-Factor Authentication has been disabled.");
        setMfaEnabled(false);
        setDisableModalOpen(false);
        setDisableOtp('');
      }
    } catch (error) {
      if (error.response?.status === 422) {
        setDisableOtpError(error.response.data.errors?.otp?.message || "Invalid OTP code.");
      } else {
        message.error(error.response?.data?.message || "Failed to disable MFA.");
      }
    } finally {
      setDisableLoading(false);
    }
  };

  const copyBackupCodes = () => {
    const text = displayedBackupCodes.join("\n");
    navigator.clipboard.writeText(text);
    message.success("Backup codes copied to clipboard!");
  };

  const downloadBackupCodes = () => {
    const content = `RSA Admin - Emergency Two-Factor Authentication Recovery Codes\nGenerated: ${new Date().toLocaleString()}\nAccount: ${profileData.email}\n\nEach code can be used once if you lose access to your authenticator app (Google Authenticator, Microsoft Authenticator, etc.):\n\n${displayedBackupCodes.map((c, i) => `${i + 1}. ${c}`).join("\n")}\n\nStore this file in a secure location.`;
    const element = document.createElement("a");
    const file = new Blob([content], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `rsa-admin-recovery-codes-${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <div className="container-fluid">
      <Breadcrumb
        style={{ marginBottom: "16px" }}
        items={[
          { title: <Link to="/dashboard">Home</Link> },
          { title: "Profile" },
        ]}
      />
      <h1 className="h3 mb-4 text-gray-800">{title}</h1>

      {/* ── Profile update card (existing business logic) ───────────────────── */}
      <Card>
        <Row justify="center">
          <Col xs={24} sm={20} md={18} lg={12}>
            <Spin spinning={profileData.loading}>
              <Form form={form} layout="vertical">
                <Form.Item
                  label={<span>First Name <span style={{ color: "red" }}>*</span></span>}
                  name="first_name"
                  validateStatus={profileData.errors?.first_name ? "error" : ""}
                  help={profileData.errors?.first_name?.message}
                >
                  <Input
                    placeholder="First Name"
                    name="first_name"
                    value={profileData?.first_name}
                    onChange={handleInput}
                  />
                </Form.Item>
                <Form.Item
                  label={<span>Last Name <span style={{ color: "red" }}>*</span></span>}
                  name="last_name"
                  validateStatus={profileData.errors?.last_name ? "error" : ""}
                  help={profileData.errors?.last_name?.message}
                >
                  <Input
                    placeholder="Last Name"
                    name="last_name"
                    value={profileData?.last_name}
                    onChange={handleInput}
                  />
                </Form.Item>
                <Form.Item
                  label={<span>Email <span style={{ color: "red" }}>*</span></span>}
                  name="email"
                  validateStatus={profileData.errors?.email ? "error" : ""}
                  help={profileData.errors?.email?.message}
                >
                  <Input
                    placeholder="Email"
                    name="email"
                    value={profileData?.email}
                    onChange={handleInput}
                  />
                </Form.Item>
                <Form.Item
                  label={<span>Phone <span style={{ color: "red" }}>*</span></span>}
                  name="phone"
                  validateStatus={profileData.errors?.phone ? "error" : ""}
                  help={profileData.errors?.phone?.message}
                >
                  <Input
                    placeholder="Phone"
                    name="phone"
                    value={profileData?.phone}
                    onChange={handleInput}
                  />
                </Form.Item>
                <Form.Item style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    type="primary"
                    style={{ marginRight: 8 }}
                    onClick={UpdateProfile}
                    loading={buttonLoading}
                  >
                    {buttonLoading ? "Processing..." : "Update"}
                  </Button>
                </Form.Item>
              </Form>
            </Spin>
          </Col>
        </Row>
      </Card>

      {/* ── Two-Factor Authentication Card ───────────────────────────────────── */}
      <Card style={{ marginTop: 24 }}>
        <Spin spinning={mfaLoading}>
          <Row justify="center">
            <Col xs={24} sm={20} md={18} lg={12}>
              <Divider>
                <SafetyCertificateOutlined style={{ marginRight: 6 }} />
                Two-Factor Authentication (Authenticator App)
              </Divider>

              {/* ── MFA Enabled View ── */}
              {mfaEnabled && mfaStep === 'idle' && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: 12 }}>
                    <Tag color="success" icon={<LockOutlined />} style={{ fontSize: 14, padding: '4px 12px' }}>
                      2FA Active & Protected
                    </Tag>
                  </div>
                  <p className="text-muted small mb-3">
                    Your account is secured with Two-Factor Authentication (TOTP).
                  </p>

                  {/* Disable 2FA button commented out as of now
                  <Button
                    danger
                    icon={<UnlockOutlined />}
                    onClick={() => {
                      setDisableOtp('');
                      setDisableOtpError('');
                      setDisableModalOpen(true);
                    }}
                  >
                    Disable 2FA
                  </Button>
                  */}
                </div>
              )}

              {/* ── MFA Disabled View ── */}
              {!mfaEnabled && mfaStep === 'idle' && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: 12 }}>
                    <Tag color="default" icon={<UnlockOutlined />} style={{ fontSize: 14, padding: '4px 12px' }}>
                      MFA Not Configured
                    </Tag>
                  </div>
                  <p className="text-muted small mb-3">
                    Protect your administrative account with two-step verification using an authenticator app (Google Authenticator, Microsoft Authenticator, etc.).
                  </p>
                  <Button type="primary" icon={<SafetyCertificateOutlined />} onClick={handleMfaSetup}>
                    Setup Authenticator App
                  </Button>
                </div>
              )}

              {/* ── Step 1: Scan QR Code ── */}
              {mfaStep === 'scan' && (
                <div style={{ textAlign: 'center' }}>
                  <p className="mb-2">
                    <strong>Step 1:</strong> Scan this QR code in your <strong>authenticator app</strong> (Google Authenticator, Microsoft Authenticator, etc.):
                  </p>
                  {mfaQrCode && (
                    <div style={{ display: 'inline-block', padding: 8, background: '#fff', border: '1px solid #ddd', borderRadius: 8, marginBottom: 12 }}>
                      <img
                        src={mfaQrCode}
                        alt="Authenticator App QR Code"
                        style={{ width: 190, height: 190, display: 'block' }}
                      />
                    </div>
                  )}
                  {mfaSecret && (
                    <div style={{ marginBottom: 16 }}>
                      <div className="text-muted small mb-1">Or enter key manually:</div>
                      <code style={{ fontSize: 13, letterSpacing: '0.12em', background: '#f5f5f5', padding: '4px 8px', borderRadius: 4, display: 'inline-block' }}>
                        {mfaSecret}
                      </code>
                    </div>
                  )}
                  <Space direction="vertical">
                    <Button
                      type="primary"
                      onClick={() => { setMfaStep('confirm'); setSetupOtpError(''); }}
                    >
                      I've scanned the QR code →
                    </Button>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => { setMfaStep('idle'); setMfaQrCode(null); setMfaSecret(null); }}
                    >
                      Cancel Setup
                    </Button>
                  </Space>
                </div>
              )}

              {/* ── Step 2: Confirm 6-Digit Code ── */}
              {mfaStep === 'confirm' && (
                <div style={{ textAlign: 'center' }}>
                  <p className="mb-2">
                    <strong>Step 2:</strong> Enter the 6-digit verification code from your authenticator app:
                  </p>
                  <form onSubmit={handleVerifySetup}>
                    <div style={{ marginBottom: 12 }}>
                      <Input
                        placeholder="000 000"
                        maxLength={6}
                        value={setupOtp}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setSetupOtp(val);
                          setSetupOtpError('');
                        }}
                        style={{ width: 200, textAlign: 'center', letterSpacing: '0.3em', fontSize: '1.25rem' }}
                        autoFocus
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        status={setupOtpError ? 'error' : ''}
                      />
                      {setupOtpError && <div style={{ color: '#ff4d4f', marginTop: 4, fontSize: 12 }}>{setupOtpError}</div>}
                    </div>
                    <Space>
                      <Button
                        type="primary"
                        htmlType="submit"
                        disabled={setupOtp.length !== 6}
                      >
                        Verify & Activate 2FA
                      </Button>
                      <Button
                        type="link"
                        onClick={() => { setMfaStep('scan'); setSetupOtp(''); setSetupOtpError(''); }}
                      >
                        ← Back
                      </Button>
                    </Space>
                  </form>
                </div>
              )}

              {/* ── Step 3: Backup Recovery Codes Display ── */}
              {mfaStep === 'backup_codes' && (
                <div>
                  <Alert
                    message="Save Your Emergency Recovery Codes"
                    description="If you lose access to your authenticator app, each of these backup codes can be used once to access your account. These codes will not be shown again."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  <div
                    style={{
                      background: "#f9f9f9",
                      padding: 16,
                      borderRadius: 8,
                      border: "1px solid #e0e0e0",
                      marginBottom: 16,
                    }}
                  >
                    <Row gutter={[12, 12]}>
                      {displayedBackupCodes.map((code, idx) => (
                        <Col span={12} key={idx}>
                          <div
                            style={{
                              background: "#fff",
                              padding: "6px 12px",
                              borderRadius: 4,
                              fontFamily: "monospace",
                              fontSize: 13,
                              fontWeight: "bold",
                              textAlign: "center",
                              border: "1px solid #d9d9d9",
                              letterSpacing: "0.08em",
                            }}
                          >
                            {code}
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>

                  <div style={{ textAlign: "center" }}>
                    <Space wrap style={{ marginBottom: 12 }}>
                      <Button icon={<CopyOutlined />} onClick={copyBackupCodes}>
                        Copy Codes
                      </Button>
                      <Button icon={<DownloadOutlined />} onClick={downloadBackupCodes}>
                        Download Text File
                      </Button>
                    </Space>
                    <div>
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={() => {
                          setMfaStep('idle');
                          setDisplayedBackupCodes([]);
                        }}
                      >
                        I have securely saved these codes
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </Spin>
      </Card>

      {/* ── Disable MFA Confirmation Modal (commented out as of now) ──
      <Modal
        title="Disable Two-Factor Authentication"
        open={disableModalOpen}
        onCancel={() => { setDisableModalOpen(false); setDisableOtp(''); setDisableOtpError(''); }}
        footer={[
          <Button key="cancel" onClick={() => { setDisableModalOpen(false); setDisableOtp(''); setDisableOtpError(''); }}>
            Cancel
          </Button>,
          <Button
            key="disable"
            danger
            loading={disableLoading}
            disabled={disableOtp.length !== 6}
            onClick={handleDisable}
          >
            Disable 2FA
          </Button>,
        ]}
      >
        <p className="small text-muted mb-3">
          Disabling 2FA makes your account less secure. Enter your current 6-digit authenticator app code to confirm:
        </p>
        <Input
          placeholder="000 000"
          maxLength={6}
          value={disableOtp}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
            setDisableOtp(val);
            setDisableOtpError('');
          }}
          style={{ textAlign: 'center', letterSpacing: '0.3em', fontSize: '1.25rem' }}
          autoFocus
          inputMode="numeric"
          autoComplete="one-time-code"
          status={disableOtpError ? 'error' : ''}
        />
        {disableOtpError && <div style={{ color: '#ff4d4f', marginTop: 4, fontSize: 12 }}>{disableOtpError}</div>}
      </Modal>
      */}
    </div>
  );
}

export default Profile;
